import type { RatedTalentCategory } from '@/../../shared/types/talent-map';
import { isClosedTalentStatus, isRatedTalentCategory, TALENT_MAP_MAX_MESSAGE_CHARS } from '@/../../shared/types/talent-map';
import { pendingLeaderChange } from '@/../../shared/talent-map-rules';
import { useChatScroll } from '@/components/chat/useChatScroll';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normalizeLang } from '@/../../shared/lang';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAutoResizeTextarea } from '@/components/ui/animated-ai-chat';
import { Send, Sparkles, Check, AlertCircle, Mic, ChevronDown, BookOpen, Volume2, VolumeX, ArrowRight } from 'lucide-react';
import { VoiceInput } from '@/components/performance/VoiceInput';
import { AiSpeakingOrb } from '@/components/performance/AiSpeakingOrb';
import { useSpokenReply } from '@/hooks/useSpokenReply';
import { AGENT_VOICE, STT_LANG } from '@/lib/speech-utils';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { MarkdownText } from '@/components/performance/MarkdownText';
import { AgentReadingPanel } from '@/components/performance/talent-map/AgentReadingPanel';
import {
  RATED_CATEGORIES, CATEGORY_CONFIG, getInitials, getAvatarColor, formatPeriodLabel,
  type TalentCategory,
} from '@/components/performance/talent-map/talentMapShared';
import {
  useTalentMapTeam, useTalentMapEvaluation, useProcessTalentMessage, useConfirmTalentCategory, useCycleConfig,
} from '@/hooks/usePerformance';
import { useAuth } from '@/contexts/SharedAuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface LocalMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  /**
   * Acción debajo del mensaje del agente. Una conversación = una persona
   * (incidente 17/09: la evaluación de Paula quedó dentro del registro de
   * Marcos): si el líder nombra a otra persona, el server no la evalúa acá y
   * devuelve a dónde ir; y al dejar a alguien en "Pendiente de evidencia" el
   * camino de vuelta es el Mapa del equipo, no seguir escribiendo.
   */
  cta?: { label: string; to: string };
}

/** No person in the URL → team picker */
function PersonPicker() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: team, isLoading } = useTalentMapTeam();

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><WinclapLoader size={48} className="text-foreground/25" /></div>;
  }

  // Solo pm o n1 evalúan desde acá; el resto de la estructura (scope
  // recursivo) se ve en solo-lectura desde el mapa del equipo / histórico.
  const members = (team ?? []).filter(m => (m.canEvaluate ?? m.isDirect) !== false);
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('performance.talent_map.evaluation_tab.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('performance.talent_map.evaluation_tab.pick_person')}</p>
      </div>
      {members.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">{t('performance.talent_map.no_direct_reports')}</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {members.map(m => {
            const fullName = `${m.name} ${m.lastName}`;
            return (
              <button
                key={m.email}
                onClick={() => navigate(`/performance/talent-map/evaluation/${encodeURIComponent(m.email)}`)}
                className="flex items-center gap-3 rounded-xl border bg-card p-4 text-left hover:bg-accent transition-colors"
              >
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold shrink-0 text-gray-700"
                  style={{ backgroundColor: getAvatarColor(fullName) }}
                >
                  {getInitials(m.name, m.lastName)}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{fullName}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {[m.role, m.impactLevel].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function TalentMapEvaluationTab() {
  const { email: encodedEmail } = useParams<{ email?: string }>();
  if (!encodedEmail) return <PersonPicker />;
  return <EvaluationChat employeeEmail={decodeURIComponent(encodedEmail)} />;
}

function EvaluationChat({ employeeEmail }: { employeeEmail: string }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [input, setInput] = useState('');
  const [localMessages, setLocalMessages] = useState<LocalMessage[]>([]);
  // Último mensaje que falló al enviarse (banner de reintento)
  const [sendError, setSendError] = useState<string | null>(null);
  const [pendingRecommendation, setPendingRecommendation] = useState<TalentCategory | null>(null);
  // Solo categorías evaluables: 'onboarding' no se confirma ni se sobreescribe (es el z.enum del router).
  const [selectedCategory, setSelectedCategory] = useState<RatedTalentCategory | null>(null);
  const [justification, setJustification] = useState('');
  const [showJustification, setShowJustification] = useState(false);
  const [showCriteria, setShowCriteria] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  // Mientras es true, el toggle del mic queda deshabilitado (ver el botón).
  const [voiceRecording, setVoiceRecording] = useState(false);
  // Respuesta hablada de la agente (pedido Sol 25/08): se enciende sola si la
  // evidencia se dictó por voz, o con el toggle de altavoz (persistido).
  const [speakerOn, setSpeakerOn] = useState(() => localStorage.getItem('talentmap.speaker') === '1');
  const lastInputWasVoiceRef = useRef(false);
  const { speak, stop: stopSpeaking, isSpeaking } = useSpokenReply();

  // Textarea que crece con el contenido (mismo arreglo que D-Talk, 14/09): era
  // un renglón fijo y la evidencia dictada quedaba escondida por debajo. El
  // adjustHeight va en un effect sobre `input` para cubrir el setInput del mic.
  const { textareaRef, adjustHeight } = useAutoResizeTextarea({ minHeight: 32, maxHeight: 160 });
  const focusAfterVoiceRef = useRef(false);
  useEffect(() => {
    adjustHeight();
    if (!focusAfterVoiceRef.current) return;
    focusAfterVoiceRef.current = false;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    el.scrollTop = el.scrollHeight;
  }, [input, adjustHeight, textareaRef]);

  const toggleSpeaker = () => {
    setSpeakerOn(prev => {
      const next = !prev;
      localStorage.setItem('talentmap.speaker', next ? '1' : '0');
      if (!next) stopSpeaking();
      return next;
    });
  };
  const lastAssistantRef = useRef<HTMLDivElement>(null);

  const { data: cycle } = useCycleConfig();
  const { data, isLoading } = useTalentMapEvaluation(employeeEmail);
  const { data: team } = useTalentMapTeam();
  const processMessage = useProcessTalentMessage();
  const confirmCategory = useConfirmTalentCategory();

  const historyFromServer = data?.record?.conversationHistory ?? [];
  const messages: LocalMessage[] = localMessages.length > 0 ? localMessages : historyFromServer;

  // Idioma de la UI → idioma del saludo automático (es/en/pt)
  const uiLang = normalizeLang(i18n.language);

  // Saludo automático (Euge 19/08): Flavia recibe al evaluador sin esperar el
  // "hola". Burbuja sintética mientras no hay historial; el backend persiste el
  // MISMO texto como primer mensaje en el primer turno real (espejo de
  // talentMapGreeting en talent-map-agent.ts — si cambiás uno, cambiá el otro).
  const greeting = useMemo<LocalMessage>(() => ({
    role: 'assistant',
    content: t('performance.talent_map.auto_greeting', {
      name: `${data?.employee.name ?? ''} ${data?.employee.lastName ?? ''}`.trim(),
    }),
    timestamp: new Date().toISOString(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [data?.employee.name, data?.employee.lastName, uiLang, t]);
  const displayMessages: LocalMessage[] = messages.length > 0 ? messages : [greeting];

  // Los indirectos (scope recursivo) no se evalúan: al histórico en solo-lectura
  useEffect(() => {
    if (data && (data.canEvaluate ?? data.isDirect) === false) {
      navigate(`/performance/talent-map/history/${encodeURIComponent(employeeEmail)}`, { replace: true });
    }
  }, [data, employeeEmail, navigate]);
  const messagesEndRef = useChatScroll([messages, processMessage.isPending]);

  // Cuando aparece la recomendación, en vez de saltar al fondo (que dejaba el
  // botón de confirmar tapando el mensaje final), llevamos el ÚLTIMO mensaje del
  // agente al tope: el líder lo lee primero y la card de confirmar queda debajo.
  useEffect(() => {
    if (pendingRecommendation) {
      lastAssistantRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pendingRecommendation]);

  useEffect(() => {
    if (historyFromServer.length > 0 && localMessages.length === 0) {
      setLocalMessages(historyFromServer);
    }
  }, [historyFromServer.length]);

  // Cambio propuesto en el chat sobre una evaluación ya cerrada (Sol 25/09):
  // la card de confirmar vuelve con esa categoría preseleccionada. Confirmar
  // con el botón es lo que la lleva al Mapa (el agente no cierra nada).
  const pendingChange = useMemo(() => {
    const c = pendingLeaderChange(data?.record);
    return isRatedTalentCategory(c) ? c : null;
  }, [data?.record]);
  useEffect(() => {
    if (pendingChange) {
      setSelectedCategory(pendingChange);
      setShowJustification(false);
    }
  }, [pendingChange]);
  // Qué categoría destaca la card: el cambio propuesto en el chat (evaluación
  // cerrada) o la recomendación de Flavia (evaluación abierta). Es la única
  // referencia para "Recomendada / Propuesta en el chat" y para pedir
  // justificación solo si el líder elige OTRA (frontend-auditor 25/09).
  const cardCategory: TalentCategory | null = pendingChange ?? pendingRecommendation;

  // Restore pending recommendation from server state. Mientras la evaluación
  // no esté cerrada (confirmed/hrbp_modified) la card con el botón tiene que
  // volver a aparecer: es la ÚNICA forma de confirmar — lo que el líder escribe
  // en el chat no cierra nada (incidente 07/09/2026).
  useEffect(() => {
    const status = data?.record?.processStatus;
    if (
      data?.record?.agentRecommendedCategory &&
      status && !isClosedTalentStatus(status) &&
      !pendingRecommendation
    ) {
      const cat = data.record.agentRecommendedCategory;
      if ((RATED_CATEGORIES as readonly string[]).includes(cat)) {
        setPendingRecommendation(cat as TalentCategory);
      }
    }
  }, [data?.record]);

  async function handleSend() {
    if (!input.trim() || processMessage.isPending) return;
    const userContent = input.trim();
    // El procedure rechaza más que esto con un 400 que se veía como "no se pudo
    // enviar" y un "Reintentar" condenado a fallar (incidente de D-Talk 01/10).
    // El aviso de abajo se deriva del largo, así que no hace falta setear nada.
    if (userContent.length > TALENT_MAP_MAX_MESSAGE_CHARS) return;
    const userMsg: LocalMessage = { role: 'user', content: userContent, timestamp: new Date().toISOString() };
    // Primer turno: sembrar el saludo sintético como en D-Talk, para que no
    // desaparezca de la vista hasta que el server devuelva el historial seedeado
    setLocalMessages(prev =>
      prev.length === 0 && historyFromServer.length === 0 ? [greeting, userMsg] : [...prev, userMsg]
    );
    setInput('');

    const wasVoice = lastInputWasVoiceRef.current;
    lastInputWasVoiceRef.current = false;
    setSendError(null);
    try {
      const result = await processMessage.mutateAsync({ employeeEmail, message: userContent, lang: uiLang });

      const switchTo = result.switchTo ?? null;
      const cta: LocalMessage['cta'] | undefined = switchTo
        ? { label: t('performance.talent_map.evaluation_tab.switch_to', { name: switchTo.name }), to: `/performance/talent-map/evaluation/${encodeURIComponent(switchTo.email)}` }
        : result.action?.action === 'mark_pending_evidence'
          ? { label: t('performance.talent_map.evaluation_tab.back_to_team_cta'), to: '/performance/talent-map/team' }
          : undefined;
      const assistantMsg: LocalMessage = {
        role: 'assistant',
        content: result.reply,
        timestamp: new Date().toISOString(),
        cta,
      };
      setLocalMessages(prev => [...prev, assistantMsg]);

      if ((wasVoice || speakerOn) && result.reply) {
        void speak(result.reply, { voice: AGENT_VOICE[uiLang], browserLang: STT_LANG[uiLang] });
      }

      if (result.action?.action === 'recommend_category' && result.agentCategory) {
        setPendingRecommendation(result.agentCategory as TalentCategory);
        setSelectedCategory(null);
      }
    } catch {
      // Sin esto el mensaje del líder quedaba pintado sin respuesta, el input
      // vacío y la única salida era recargar la página (mismo bug que tenía
      // el chat de D-Talk). Devolvemos el texto y sacamos la burbuja huérfana.
      setLocalMessages(prev => prev.slice(0, -1));
      setInput(userContent);
      setSendError(userContent);
    }
  }

  function handleCategorySelect(cat: RatedTalentCategory) {
    setSelectedCategory(cat);
    setShowJustification(cat !== cardCategory);
  }

  async function handleConfirm() {
    if (!selectedCategory) return;
    if (showJustification && justification.trim().length < 5) {
      toast.error(t('performance.talent_map.justification_prompt'));
      return;
    }
    await confirmCategory.mutateAsync({
      employeeEmail,
      category: selectedCategory,
      justification: showJustification ? justification.trim() : undefined,
    });
    setPendingRecommendation(null);
    setSelectedCategory(null);
    setJustification('');
    setShowJustification(false);
    // Cierre: confirmamos y volvemos a la lista del equipo para que el líder
    // elija con quién sigue (en vez de que el agente pida el siguiente clapper).
    // El toast de éxito lo tira useConfirmTalentCategory (uno solo por click)
    navigate('/performance/talent-map/team');
  }

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><WinclapLoader size={48} className="text-foreground/25" /></div>;
  }

  if (!data) {
    return <div className="py-16 text-center text-muted-foreground">{t('performance.talent_map.not_found')}</div>;
  }

  const { employee, record } = data;
  const fullName = `${employee.name} ${employee.lastName}`;
  const teamRow = team?.find(m => m.email === employeeEmail);
  const eligibility = record?.eligibilityStatus ?? teamRow?.eligibilityStatus ?? 'eligible';
  const isOnboarding = eligibility === 'onboarding';
  const isInactive = eligibility === 'inactive';
  const isLicense = eligibility === 'license';
  const isConfirmed = isClosedTalentStatus(record?.processStatus);
  const isPendingEvidence = record?.processStatus === 'pending_evidence';
  const myInitials = getInitials(user?.name, user?.name?.split(' ')[1]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
      {/* ─── Center: header + chat ─── */}
      <div className="flex flex-col min-w-0 h-[calc(100vh-13rem)] min-h-[480px]">
        <div className="mb-3">
          <h1 className="text-2xl font-bold tracking-tight">{t('performance.talent_map.evaluation_tab.title')}</h1>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-teal-600 mt-1">{formatPeriodLabel(cycle)}</p>
        </div>

        {/* Person header card */}
        <Card className="mb-3">
          <CardContent className="pt-3 pb-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold shrink-0 text-gray-700"
                  style={{ backgroundColor: getAvatarColor(fullName) }}
                >
                  {getInitials(employee.name, employee.lastName)}
                </div>
                <div className="min-w-0">
                  <p className="font-bold truncate">{fullName}</p>
                  <p className="text-sm text-teal-600 truncate">
                    {[employee.role ?? employee.impactLevel, employee.area].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </div>
              <div className="text-right text-xs text-muted-foreground space-y-0.5 shrink-0">
                <p>{t('performance.talent_map.evaluation_tab.people_manager')}: {user?.name ?? user?.email?.split('@')[0]}</p>
                {employee.startDate && <p>{t('performance.talent_map.evaluation_tab.joined')}: {employee.startDate}</p>}
                <p className={cn('font-medium inline-flex items-center gap-1', isInactive ? 'text-red-600' : isOnboarding ? 'text-blue-600' : isLicense ? 'text-violet-600' : 'text-emerald-600')}>
                  <span className={cn('h-1.5 w-1.5 rounded-full', isInactive ? 'bg-red-500' : isOnboarding ? 'bg-blue-500' : isLicense ? 'bg-violet-500' : 'bg-emerald-500')} />
                  {isInactive
                    ? t('performance.talent_map.eligibility_inactive', { defaultValue: 'No elegible — Inactivo' })
                    : isOnboarding
                      ? t('performance.talent_map.category_onboarding')
                      : isLicense
                        ? t('performance.talent_map.eligibility_license')
                        : t('performance.talent_map.evaluation_tab.eligible_badge')}
                </p>
              </div>
            </div>
            {/* Solo si de verdad no hay otra evaluación de esta persona bajo otro
                líder: era un texto fijo que afirmaba "sin cambios" para todo el mundo. */}
            {(data.otherEvaluations ?? []).length === 0 && (
              <p className="text-[11px] text-muted-foreground/70 mt-2 pt-2 border-t text-center">
                {t('performance.talent_map.evaluation_tab.no_leader_changes')}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Criterios del framework de Impact Levels del nivel (§8 paso 2) */}
        {data.levelFramework && (
          <Card className="mb-3">
            <button
              type="button"
              onClick={() => setShowCriteria(v => !v)}
              className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <BookOpen className="h-4 w-4 text-teal-600" />
                {t('performance.talent_map.evaluation_tab.level_criteria', {
                  defaultValue: 'Criterios de Impact Levels — {{level}}',
                  level: data.levelFramework.level,
                })}
              </span>
              <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', showCriteria && 'rotate-180')} />
            </button>
            {showCriteria && (
              <CardContent className="pt-0 pb-4 space-y-3">
                {([
                  ['Effective', data.levelFramework.effective],
                  ['Affective', data.levelFramework.affective],
                  ['Perspective', data.levelFramework.perspective],
                ] as const).map(([dim, text]) =>
                  text ? (
                    <div key={dim} className="space-y-0.5">
                      <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-teal-600">{dim}</p>
                      <p className="text-sm text-foreground/90">{text}</p>
                    </div>
                  ) : null
                )}
              </CardContent>
            )}
          </Card>
        )}

        {isOnboarding && (
          <div className="mb-3 rounded-xl bg-blue-50 border border-blue-200 dark:bg-blue-950/20 p-3 flex items-center gap-2 text-sm text-blue-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {t('performance.talent_map.evaluation_tab.onboarding_banner')}
          </div>
        )}

        {/* Otra evaluación REAL de la misma persona bajo otro líder (pm y N+1 en
            paralelo, caso Lucía Jorge 18/09): avisar antes de que haya dos confirmadas. */}
        {/* Recategorizada por el cron al promocionar (systemLock): el líder veía
            "Confirmada" y no distinguía su propia decisión de la del sistema. */}
        {data.record?.systemLock && (
          <div className="mb-3 rounded-xl bg-violet-50 border border-violet-200 dark:bg-violet-950/20 p-3 flex items-center gap-2 text-sm text-violet-800 dark:text-violet-200">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{t('performance.talent_map.evaluation_tab.system_lock_banner')}</span>
          </div>
        )}

        {(data.otherEvaluations ?? []).map((o: { leaderEmail: string; processStatus: string; finalCategory: string | null; updatedAt: string | null }) => (
          <div key={o.leaderEmail} className="mb-3 rounded-xl bg-amber-50 border border-amber-200 dark:bg-amber-950/20 p-3 flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>
              {t('performance.talent_map.evaluation_tab.other_evaluation', {
                name: teamRow && teamRow.leaderEmail === o.leaderEmail && teamRow.leaderName ? teamRow.leaderName : o.leaderEmail.split('@')[0],
                status: t(`performance.talent_map.status_${o.processStatus}`),
                category: o.finalCategory && (o.finalCategory in CATEGORY_CONFIG) ? t(CATEGORY_CONFIG[o.finalCategory as TalentCategory].labelKey) : '—',
                date: o.updatedAt ? format(new Date(o.updatedAt), 'dd/MM') : '—',
              })}
            </span>
          </div>
        ))}

        {isLicense && (
          <div className="mb-3 rounded-xl bg-violet-50 border border-violet-200 dark:bg-violet-950/20 p-3 flex items-center gap-2 text-sm text-violet-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {t('performance.talent_map.evaluation_tab.license_banner')}
          </div>
        )}

        {isInactive && (
          <div className="mb-3 rounded-xl bg-red-50 border border-red-200 dark:bg-red-950/20 p-3 flex items-center gap-2 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {t('performance.talent_map.evaluation_tab.inactive_banner', {
              defaultValue: 'Esta persona está inactiva en la nómina y no puede ser evaluada. Su histórico queda disponible solo para People.',
            })}
          </div>
        )}

        {/* Messages — sin historial se muestra el saludo automático de Flavia */}
        <div className="flex-1 overflow-y-auto space-y-5 pr-1">
          {displayMessages.map((msg, i) => (
            <div
              key={i}
              ref={msg.role === 'assistant' && i === displayMessages.length - 1 ? lastAssistantRef : undefined}
              className={cn('flex items-end gap-2', msg.role === 'user' ? 'justify-end' : 'justify-start')}
            >
              {msg.role === 'assistant' && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted border shrink-0 mb-5">
                  <Sparkles className="h-4 w-4 text-foreground/70" />
                </div>
              )}
              <div className={cn(
                'max-w-[85%] rounded-2xl px-4 py-3 text-[15px]',
                msg.role === 'user'
                  ? 'bg-primary text-primary-foreground rounded-br-sm'
                  : 'bg-card border text-foreground rounded-bl-sm',
              )}>
                {msg.role === 'user'
                  ? <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  : <MarkdownText className="leading-relaxed">{msg.content}</MarkdownText>}
                {msg.cta && (
                  <Button size="sm" variant="outline" className="mt-2 gap-1.5" onClick={() => navigate(msg.cta!.to)}>
                    {msg.cta.label}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                )}
                <p className={cn(
                  'text-xs mt-1',
                  msg.role === 'user' ? 'text-primary-foreground/60' : 'text-muted-foreground',
                )}>
                  {format(new Date(msg.timestamp), 'HH:mm')}
                </p>
              </div>
              {msg.role === 'user' && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-bold shrink-0 mb-5">
                  {myInitials}
                </div>
              )}
            </div>
          ))}

          {processMessage.isPending && (
            <div className="flex items-end gap-2 justify-start">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted border shrink-0">
                <Sparkles className="h-4 w-4 text-foreground/70" />
              </div>
              <div className="bg-card border rounded-2xl rounded-bl-sm px-4 py-3">
                <WinclapLoader size={20} />
              </div>
            </div>
          )}
          {/* Card de confirmar categoría — DENTRO del scroll: fluye debajo del
              último mensaje del agente (antes tapaba el mensaje final). */}
          {cardCategory && (pendingChange || !isConfirmed) && (
            <Card className={pendingChange ? 'border-amber-200 bg-amber-50/50 dark:bg-amber-950/10' : 'border-teal-200 bg-teal-50/50 dark:bg-teal-950/10'}>
              <CardContent className="pt-4 space-y-3">
                <p className="text-sm font-semibold">
                  {pendingChange
                    ? t(record?.processStatus === 'hrbp_modified' ? 'performance.talent_map.reconfirm_question_hrbp' : 'performance.talent_map.reconfirm_question', {
                        proposed: t(CATEGORY_CONFIG[pendingChange].labelKey),
                        current: record?.finalCategory ? t(CATEGORY_CONFIG[record.finalCategory as TalentCategory].labelKey) : '',
                      })
                    : t('performance.talent_map.recommend_question', {
                        category: t(CATEGORY_CONFIG[cardCategory].labelKey),
                      })}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {RATED_CATEGORIES.map(cat => {
                    const c = CATEGORY_CONFIG[cat];
                    const isRec = cat === cardCategory;
                    const isChosen = selectedCategory === cat;
                    return (
                      <button
                        key={cat}
                        onClick={() => handleCategorySelect(cat)}
                        className={cn(
                          'relative rounded-xl border overflow-hidden text-left transition-all',
                          isChosen ? `${c.ring} bg-white dark:bg-card shadow-xs` : 'border-border bg-card hover:bg-accent',
                        )}
                      >
                        <div className={`h-1 w-full ${c.bar}`} />
                        <div className="p-3 flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-bold">{t(c.labelKey)}</p>
                            {isRec && <p className="text-xs text-muted-foreground mt-0.5">{t(pendingChange ? 'performance.talent_map.proposed_in_chat_label' : 'performance.talent_map.recommended_label')}</p>}
                          </div>
                          {isChosen && <Check className="h-4 w-4 text-foreground shrink-0 mt-0.5" />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {showJustification && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-muted-foreground">{t('performance.talent_map.justification_prompt')}</p>
                    <Textarea
                      value={justification}
                      onChange={e => setJustification(e.target.value)}
                      placeholder={t('performance.talent_map.justification_placeholder')}
                      rows={2}
                      className="text-sm resize-none"
                    />
                  </div>
                )}

                <Button
                  onClick={handleConfirm}
                  disabled={!selectedCategory || confirmCategory.isPending}
                  size="sm"
                  className="w-full gap-2"
                >
                  {confirmCategory.isPending ? <WinclapLoader size={14} /> : <Check className="h-4 w-4" />}
                  {t('performance.talent_map.confirm_category')}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  {t('performance.talent_map.confirm_only_button_hint')}
                </p>
              </CardContent>
            </Card>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Status banners */}
        {isConfirmed && !pendingChange && (
          <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/20 p-3 flex items-center gap-2 text-sm text-emerald-700">
            <Check className="h-4 w-4 shrink-0" />
            {t('performance.talent_map.evaluation_confirmed')}
          </div>
        )}
        {pendingChange && (
          <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 dark:bg-amber-950/20 p-3 flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {t(record?.processStatus === 'hrbp_modified' ? 'performance.talent_map.reconfirm_banner_hrbp' : 'performance.talent_map.reconfirm_banner', {
              proposed: t(CATEGORY_CONFIG[pendingChange].labelKey),
              current: record?.finalCategory ? t(CATEGORY_CONFIG[record.finalCategory as TalentCategory].labelKey) : '',
            })}
          </div>
        )}
        {isPendingEvidence && !isConfirmed && (
          <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 dark:bg-amber-950/20 p-3 flex items-center gap-2 text-sm text-amber-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {t('performance.talent_map.pending_evidence_banner')}
          </div>
        )}

        {/* Input bar. La voz abre un panel con transcripción EDITABLE antes de
            registrarse como evidencia (§5.3). Se oculta para inactivos (§7.2). */}
        {!isInactive && !isLicense && (
        <>
        {voiceOpen && (
          <div className="mt-3 rounded-xl border bg-card p-3 space-y-1.5">
            <p className="text-xs text-muted-foreground">
              {t('performance.talent_map.evaluation_tab.voice_hint', {
                defaultValue: 'Grabá tu evidencia. Revisá y editá la transcripción antes de usarla.',
              })}
            </p>
            <VoiceInput
              variant="panel"
              lang={STT_LANG[uiLang]}
              onRecordingChange={(rec) => {
                setVoiceRecording(rec);
                if (rec) stopSpeaking();
              }}
              onTranscript={text => {
                lastInputWasVoiceRef.current = true;
                focusAfterVoiceRef.current = true;
                // `maxLength` no alcanza a un setState y dictar es donde uno se pasa
                setInput(prev => (prev ? `${prev} ${text}` : text).slice(0, TALENT_MAP_MAX_MESSAGE_CHARS));
                setVoiceOpen(false);
              }}
            />
          </div>
        )}
        <AiSpeakingOrb speaking={isSpeaking} floating size={96} />
        {input.trim().length > TALENT_MAP_MAX_MESSAGE_CHARS && (
          <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 dark:border-red-900 dark:bg-red-950/20">
            <p className="text-xs text-red-700 dark:text-red-300">
              {t('performance.talent_map.evaluation_tab.message_too_long', { chars: input.trim().length, max: TALENT_MAP_MAX_MESSAGE_CHARS })}
            </p>
          </div>
        )}
        {sendError && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 dark:border-red-900 dark:bg-red-950/20">
            <p className="text-xs text-red-700 dark:text-red-300">
              {t('performance.talentmapevaluationtab.no_se_pudo_enviar_el_mensaje')}
            </p>
            <Button
              size="sm" variant="outline" className="h-7 shrink-0 text-xs"
              disabled={processMessage.isPending}
              onClick={() => handleSend()}
            >
              {t('common.retry')}
            </Button>
          </div>
        )}
        {!voiceRecording && input.length > TALENT_MAP_MAX_MESSAGE_CHARS * 0.9 && input.length <= TALENT_MAP_MAX_MESSAGE_CHARS && (
          <p className="mt-2 text-right text-[10px] tabular-nums text-muted-foreground">
            {t('performance.dtalk.conv.chars_left', { restantes: TALENT_MAP_MAX_MESSAGE_CHARS - input.length, max: TALENT_MAP_MAX_MESSAGE_CHARS })}
          </p>
        )}
        {/* rounded-2xl y no rounded-full: con varias líneas la píldora se estiraba raro */}
        <div className="mt-3 flex items-end gap-2 rounded-2xl border bg-card px-4 py-2">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={t('performance.talent_map.evaluation_tab.input_placeholder')}
            rows={1}
            maxLength={TALENT_MAP_MAX_MESSAGE_CHARS}
            className="flex-1 resize-none text-sm border-0 shadow-none focus-visible:ring-0 min-h-0 max-h-40 overflow-y-auto py-1.5 px-0 bg-transparent"
            disabled={processMessage.isPending}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={toggleSpeaker}
            className={cn('rounded-full shrink-0', speakerOn && 'text-teal-600 bg-teal-50 dark:bg-teal-950/30', isSpeaking && 'animate-pulse')}
            title={isSpeaking ? t('performance.dtalk.conv.stop_reading') : t('performance.dtalk.conv.read_aloud')}
          >
            {speakerOn || isSpeaking ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          {/* Deshabilitado mientras graba: cerrar el panel en pleno dictado lo
              desmontaba y el texto caía en el input SIN pasar por la revisión
              (§5.3). La grabación se termina con el ✓/✕ de la barra. */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={voiceRecording}
            onClick={() => setVoiceOpen(v => !v)}
            className={cn('rounded-full shrink-0', voiceOpen && 'text-teal-600 bg-teal-50 dark:bg-teal-950/30')}
            title={t('performance.talent_map.evaluation_tab.voice_button', { defaultValue: 'Evidencia por voz' })}
          >
            <Mic className="h-4 w-4" />
          </Button>
          <Button
            onClick={handleSend}
            disabled={!input.trim() || processMessage.isPending}
            size="icon"
            className="rounded-full h-9 w-9 shrink-0"
          >
            {processMessage.isPending ? <WinclapLoader size={14} /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        </>
        )}
      </div>

      {/* ─── Right panel ─── */}
      <div className="lg:sticky lg:top-4">
        <AgentReadingPanel record={record} />
      </div>
    </div>
  );
}
